import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  CalendarDaysIcon,
  MapPinIcon,
  ClockIcon,
  TableCellsIcon,
  ArrowsRightLeftIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  TruckIcon,
  IdentificationIcon,
  PlusCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  CameraIcon,
  VideoCameraSlashIcon
} from "@heroicons/react/24/outline";
import { liveTrackingAPI } from '../services/employee.service';
import { liveTrackingWS } from '../services/liveTrackingWebSocket';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const EmployeeLiveTracking = () => {
  const [currentPos, setCurrentPos] = useState({ lat: 12.9716, lng: 77.5946 });
  const [isSyncing, setIsSyncing] = useState(false);
  const [empCode, setEmpCode] = useState('');
  const [locationStatus, setLocationStatus] = useState('Initializing...');
  const [permissionGranted, setPermissionGranted] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseDescription, setPauseDescription] = useState('');
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const watchIdRef = useRef(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('daily');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [locationLogs, setLocationLogs] = useState([]);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [capturedSelfie, setCapturedSelfie] = useState(null);
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [selfieLoading, setSelfieLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const handleCheckIn = () => {
    setShowSelfieModal(true);
  };

  const handleCheckInWithSelfie = (selfieData = null) => {
    setIsCheckedIn(true);
    const newLog = {
      date: new Date().toLocaleDateString(),
      inPunch: new Date().toLocaleTimeString(),
      outPunch: '',
      siteLocation: 'Field Location',
      status: 'Checked In',
      selfie: selfieData
    };
    setLocationLogs([newLog, ...locationLogs]);
    setShowSelfieModal(false);
    setCapturedSelfie(null);
    stopWebcam();
  };

  const handleCheckOut = () => {
    setIsCheckedIn(false);
    const updatedLogs = locationLogs.map((log, index) => {
      if (index === 0 && !log.outPunch) {
        return {
          ...log,
          outPunch: new Date().toLocaleTimeString(),
          status: 'Checked Out'
        };
      }
      return log;
    });
    setLocationLogs(updatedLogs);
  };

  useEffect(() => {
    const checkDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputExists = devices.some(device => device.kind === 'videoinput');
        setHasCamera(videoInputExists);
      } catch (err) {
        console.error("Error enumerating devices:", err);
        setHasCamera(false);
      }
    };
    checkDevices();
  }, []);

  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsWebcamActive(false);
  };

  const startWebcam = async () => {
    try {
      setSelfieLoading(true);
      const constraints = { 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;
        await videoRef.current.play();
        setIsWebcamActive(true);
      }
    } catch (error) {
      console.error("Camera Error:", error);
      alert('Camera access denied. Please enable camera permissions.');
    } finally {
      setSelfieLoading(false);
    }
  };

  const captureSelfie = () => {
    if (isWebcamActive && videoRef.current) {
      const videoElement = videoRef.current;
      const canvasElement = canvasRef.current;
      const context = canvasElement.getContext('2d');
      
      canvasElement.width = videoElement.videoWidth;
      canvasElement.height = videoElement.videoHeight;
      
      context.translate(canvasElement.width, 0);
      context.scale(-1, 1);
      context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);
      
      const imageData = canvasElement.toDataURL('image/jpeg', 0.8);
      setCapturedSelfie(imageData);
      stopWebcam();
    }
  };

  const downloadExcelReport = () => {
    const data = locationLogs.map(log => ({
      'Date': log.date,
      'In-Punch': log.inPunch,
      'Out-Punch': log.outPunch || 'N/A',
      'Site Location': log.siteLocation,
      'Status': log.status
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Location Logs');
    
    const fileName = reportPeriod === 'custom' && customDateRange.start && customDateRange.end 
      ? `location-report-${customDateRange.start}-to-${customDateRange.end}.xlsx`
      : `location-report-${reportPeriod}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
  };

  const downloadPDFReport = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Location & Activity Report', 20, 20);
    
    doc.setFontSize(12);
    const periodText = reportPeriod === 'custom' && customDateRange.start && customDateRange.end 
      ? `Custom Range: ${customDateRange.start} to ${customDateRange.end}`
      : `${reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}`;
    doc.text(`Report Period: ${periodText}`, 20, 35);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Employee: ${empCode}`, 20, 55);
    
    const tableData = locationLogs.map(log => [
      log.date,
      log.inPunch,
      log.outPunch || 'N/A',
      log.siteLocation,
      log.status
    ]);
    
    doc.autoTable({
      head: [['Date', 'In-Punch', 'Out-Punch', 'Site Location', 'Status']],
      body: tableData,
      startY: 65,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] }
    });
    
    const fileName = reportPeriod === 'custom' && customDateRange.start && customDateRange.end 
      ? `location-report-${customDateRange.start}-to-${customDateRange.end}.pdf`
      : `location-report-${reportPeriod}.pdf`;
    
    doc.save(fileName);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const initMap = () => {
      if (permissionGranted && mapRef.current && !map && window.L) {
        try {
          const leafletMap = window.L.map(mapRef.current).setView([currentPos.lat, currentPos.lng], 15);
          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(leafletMap);
          
          const customIcon = window.L.divIcon({
            html: `<div class="w-8 h-8 rounded-full border-2 border-white shadow-lg ${isTracking ? 'bg-green-500' : 'bg-red-500'} flex items-center justify-center"><div class="w-3 h-3 bg-white rounded-full"></div></div>`,
            className: 'custom-marker',
            iconSize: [32, 32],
            iconAnchor: [16, 16]
          });
          
          const leafletMarker = window.L.marker([currentPos.lat, currentPos.lng], { icon: customIcon }).addTo(leafletMap);
          setMap(leafletMap);
          setMarker(leafletMarker);
        } catch (error) {
          console.error('Failed to initialize map:', error);
        }
      }
    };

    if (permissionGranted && !window.L) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setTimeout(initMap, 100);
      document.head.appendChild(script);
    } else if (permissionGranted && window.L) {
      initMap();
    }
  }, [permissionGranted, empCode, isTracking]);

  useEffect(() => {
    if (map && marker && currentPos && window.L) {
      try {
        map.setView([currentPos.lat, currentPos.lng], 15);
        marker.setLatLng([currentPos.lat, currentPos.lng]);
        
        const customIcon = window.L.divIcon({
          html: `<div class="w-8 h-8 rounded-full border-2 border-white shadow-lg ${isTracking ? 'bg-green-500' : 'bg-red-500'} flex items-center justify-center"><div class="w-3 h-3 bg-white rounded-full"></div></div>`,
          className: 'custom-marker',
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        });
        marker.setIcon(customIcon);
      } catch (error) {
        console.error('Failed to update map:', error);
      }
    }
  }, [currentPos, map, marker, isTracking, empCode]);

  useEffect(() => {
    const storedEmpCode = localStorage.getItem('emp_code');
    if (storedEmpCode && storedEmpCode !== 'null' && storedEmpCode !== '') {
      setEmpCode(storedEmpCode);
      const savedPermission = localStorage.getItem(`location_permission_${storedEmpCode}`);
      
      if (savedPermission === 'granted') {
        setPermissionGranted(true);
        setLocationStatus('Permission granted - Starting GPS...');
        setTimeout(() => initializeTracking(storedEmpCode, true), 2000);
      } else if (savedPermission === 'denied') {
        setPermissionGranted(false);
        setLocationStatus('Location permission denied');
      } else {
        setShowPermissionModal(true);
      }
    } else {
      setLocationStatus('Please log in to access live tracking');
    }
  }, []);

  const handlePermissionResponse = async (granted) => {
    setShowPermissionModal(false);
    setPermissionGranted(granted);
    localStorage.setItem(`location_permission_${empCode}`, granted ? 'granted' : 'denied');
    
    if (granted) {
      setLocationStatus('Permission granted - Starting GPS...');
      setTimeout(() => initializeTracking(empCode, true), 2000);
    } else {
      setLocationStatus('Location tracking disabled by user');
    }
  };

  const initializeTracking = (employeeCode, hasPermission) => {
    if (!navigator.geolocation) {
      setLocationStatus('GPS not supported by browser');
      return;
    }

    if (!employeeCode || employeeCode === '' || employeeCode === 'null') {
      setLocationStatus('Employee code not found - please refresh page');
      return;
    }

    if (!hasPermission) {
      setLocationStatus('Location permission required');
      return;
    }

    setLocationStatus('Starting GPS tracking...');
    setIsTracking(true);
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const newPos = { lat: position.coords.latitude, lng: position.coords.longitude };
        setCurrentPos(newPos);
        setIsSyncing(true);
        setLocationStatus('Live tracking active - Location synced');
        setLocationHistory(prev => [...prev.slice(-10), newPos]);
        
        try {
          await liveTrackingAPI.updateLocation(employeeCode, newPos.lat, newPos.lng);
        } catch (error) {
          console.error('Failed to sync location:', error);
        }
        setTimeout(() => setIsSyncing(false), 1500);
      },
      (error) => {
        console.error('GPS error:', error);
        setLocationStatus(`GPS Error: ${error.message}`);
        setIsTracking(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (isPaused && pauseStartTime) {
      const timer = setInterval(() => {
        const elapsedMinutes = (Date.now() - pauseStartTime) / 60000;
        if (elapsedMinutes >= 30) {
          setIsPaused(false);
          setPauseStartTime(null);
          alert('Your 30-minute pause limit has expired. Tracking has been resumed.');
        }
      }, 60000);
      return () => clearInterval(timer);
    }
  }, [isPaused, pauseStartTime]);

  const handlePauseClick = () => {
    if (isPaused) {
      handleResumeTracking();
    } else {
      setShowPauseModal(true);
    }
  };

  const handleStopClick = () => {
    setShowPauseModal(true);
  };

  const handleSubmitPause = async () => {
    if (!pauseDescription.trim() || pauseDescription.trim().length < 5) {
      alert('Description must be at least 5 characters long.');
      return;
    }

    try {
      const managerCode = localStorage.getItem('manager_code') || 'MAN-001';
      await liveTrackingAPI.pauseTracking(empCode, managerCode, pauseDescription);
      
      setIsPaused(true);
      setPauseStartTime(Date.now());
      setShowPauseModal(false);
      setLocationStatus('Tracking paused - ' + pauseDescription);
      setPauseDescription('');
    } catch (error) {
      console.error('Failed to pause tracking:', error);
      alert('Failed to pause tracking. Please try again.');
    }
  };

  const handleResumeTracking = () => {
    setIsPaused(false);
    setPauseStartTime(null);
    setPauseDescription('');
    setLocationStatus('Live tracking active - Location synced');
    setShowPauseModal(false);
  };

  const handleStopTracking = async () => {
    if (!pauseDescription.trim() || pauseDescription.trim().length < 5) {
      alert('Description must be at least 5 characters long.');
      return;
    }

    try {
      const managerCode = localStorage.getItem('manager_code') || 'MAN-001';
      await liveTrackingAPI.stopTracking(empCode, managerCode, pauseDescription);
      
      setIsTracking(false);
      setIsPaused(false);
      setPauseStartTime(null);
      setLocationStatus('Tracking stopped - ' + pauseDescription);

      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }

      setShowPauseModal(false);
      setPauseDescription('');
    } catch (error) {
      console.error('Failed to stop tracking:', error);
      alert('Failed to stop tracking. Please try again.');
    }
  };

  useEffect(() => {
    if (empCode && isTracking && !isPaused) {
      liveTrackingWS.connect(empCode);
      const handleLocationUpdate = (data) => {
        if (data.emp_code === empCode) {
          try {
            const locationData = JSON.parse(data.data);
            setCurrentPos({ lat: locationData.lat, lng: locationData.lng });
          } catch (error) {
            console.error('Error parsing location data:', error);
          }
        }
      };
      liveTrackingWS.on('locationUpdate', handleLocationUpdate);
      return () => {
        liveTrackingWS.off('locationUpdate', handleLocationUpdate);
      };
    }
  }, [empCode, isTracking, isPaused]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      liveTrackingWS.disconnect();
      stopWebcam();
    };
  }, []);

  return (
    <div className="py-4 space-y-4">
      <style jsx>{`
        .custom-marker {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-div-icon {
          background: transparent;
          border: none;
        }
      `}</style>
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-800">My Live Location</h2>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <ShieldCheckIcon className="h-3 w-3 text-green-500" /> Employee ID: {empCode}
          </p>
          {permissionGranted && (
            <p className="text-xs font-mono text-blue-600 mt-1">
              📍 {currentPos.lat.toFixed(6)}, {currentPos.lng.toFixed(6)}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-full border border-slate-200">
            <SignalIcon className={`h-3 w-3 ${isTracking && !isPaused ? 'text-green-500 animate-pulse' : isPaused ? 'text-yellow-500' : 'text-slate-400'}`} />
            <span className="text-[10px] font-bold text-slate-600 uppercase">
              {isTracking && !isPaused ? 'Live Tracking' : isPaused ? 'Paused' : 'Tracking Off'}
            </span>
          </div>
          
          {isTracking && (
            <div className="flex gap-2">
              <button
                onClick={handlePauseClick}
                className="px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-lg hover:bg-yellow-600 transition"
              >
                {isPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                onClick={handleStopClick}
                className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 transition"
              >
                Stop
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="h-96 w-full relative bg-gradient-to-br from-blue-50 to-indigo-100 rounded-[24px] overflow-hidden border border-slate-200 z-0">
        {permissionGranted ? (
          <div className="w-full h-full relative">
            <div ref={mapRef} className="w-full h-full rounded-[24px]" />
            
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg p-3 shadow-lg max-w-xs">
              <p className="text-xs text-slate-600 mb-1">Live Coordinates:</p>
              <p className="font-mono text-sm font-bold text-blue-600">
                {currentPos.lat.toFixed(6)}, {currentPos.lng.toFixed(6)}
              </p>
              <div className={`mt-2 px-2 py-1 rounded text-xs font-medium text-center ${isTracking ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {isTracking ? '🟢 Live Tracking' : '🔴 Tracking Stopped'}
              </div>
            </div>
            
            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-lg p-3 shadow-lg">
              <div className={`px-4 py-2 rounded-full text-sm font-medium text-center ${
                locationStatus.includes('active') || locationStatus.includes('synced') ? 'bg-green-100 text-green-800' :
                locationStatus.includes('Error') || locationStatus.includes('disabled') ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {locationStatus}
              </div>
            </div>
            
            {isTracking && (
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur rounded-lg p-2 shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs font-bold text-green-600">LIVE</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center h-full flex flex-col justify-center">
            <MapPinIcon className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">Location Access Required</h3>
            <p className="text-sm text-slate-600 mb-4">Enable location tracking to start live monitoring</p>
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              locationStatus.includes('active') || locationStatus.includes('synced') ? 'bg-green-100 text-green-800' :
              locationStatus.includes('Error') || locationStatus.includes('disabled') ? 'bg-red-100 text-red-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {locationStatus}
            </div>
          </div>
        )}
      </div>

      {/* Location Punching Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Field Location Punching</h2>
          <div className="flex gap-2">
            <button
              onClick={downloadPDFReport}
              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              PDF
            </button>
            <button
              onClick={downloadExcelReport}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              Excel
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-bold text-slate-800 mb-2">Employee</h3>
            <p className="text-sm text-slate-600">{empCode}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-bold text-slate-800 mb-2">Current Session</h3>
            <p className="text-lg font-mono text-green-600">{currentTime.toLocaleTimeString()}</p>
            <button
              onClick={isCheckedIn ? handleCheckOut : handleCheckIn}
              className={`mt-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                isCheckedIn 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isCheckedIn ? 'CHECK-OUT' : 'START CHECK-IN'}
            </button>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-bold text-slate-800 mb-2">Current Site</h3>
            <p className="text-sm text-slate-600">{isCheckedIn ? 'Field Location' : 'Not at any site'}</p>
            <div className="mt-2">
              <span className="text-xs font-medium text-slate-500">Punch Status</span>
              <p className={`text-sm font-medium ${
                isCheckedIn ? 'text-green-600' : 'text-red-600'
              }`}>
                {isCheckedIn ? 'Logged In' : 'Logged Out'}
              </p>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-bold text-slate-800 mb-2">Today's Date</h3>
            <p className="text-sm text-slate-600">
              {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                weekday: 'long' 
              })}
            </p>
          </div>
        </div>

        {/* Report Period Selection */}
        <div className="bg-slate-50 p-4 rounded-lg mb-6">
          <div className="flex items-center gap-4 mb-4">
            <CalendarDaysIcon className="h-5 w-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Report Period</h3>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {['daily', 'weekly', 'monthly', 'quarterly', 'halfyearly', 'yearly'].map((period) => (
              <button
                key={period}
                onClick={() => {
                  setReportPeriod(period);
                  setShowCustomDatePicker(false);
                }}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
                  reportPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
            <button
              onClick={() => {
                setReportPeriod('custom');
                setShowCustomDatePicker(true);
              }}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
                reportPeriod === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Custom Range
            </button>
          </div>
          
          {showCustomDatePicker && (
            <div className="flex gap-4 items-center">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Location & Activity Logs */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Location & Activity Logs</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-slate-200 rounded-lg">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700">Date</th>
                  <th className="border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700">In-Punch</th>
                  <th className="border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700">Out-Punch</th>
                  <th className="border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700">Site Location</th>
                  <th className="border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {locationLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="border border-slate-200 px-4 py-8 text-center text-slate-500">
                      No Logs Found<br />
                      <span className="text-sm">Please check-in to record your first site location.</span>
                    </td>
                  </tr>
                ) : (
                  locationLogs.map((log, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="border border-slate-200 px-4 py-3 text-sm text-slate-700">{log.date}</td>
                      <td className="border border-slate-200 px-4 py-3 text-sm text-slate-700">{log.inPunch}</td>
                      <td className="border border-slate-200 px-4 py-3 text-sm text-slate-700">{log.outPunch || '-'}</td>
                      <td className="border border-slate-200 px-4 py-3 text-sm text-slate-700">{log.siteLocation}</td>
                      <td className="border border-slate-200 px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          log.status === 'Checked In' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showPauseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 relative z-[10000]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">
                {isPaused ? 'Resume Tracking' : 'Pause or Stop Tracking'}
              </h3>
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseDescription('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {!isPaused && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-bold">Note:</span> Provide a reason for pausing or stopping tracking. Your manager will be notified.
                </p>
              </div>
            )}
            
            {!isPaused && (
              <div className="mb-6">
                <label className="text-sm font-bold text-slate-700 block mb-2">
                  Reason for Pause/Stop
                </label>
                <textarea
                  value={pauseDescription}
                  onChange={(e) => setPauseDescription(e.target.value)}
                  placeholder="Enter reason (minimum 5 characters)..."
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm resize-none"
                  rows="4"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className={`text-xs ${
                    pauseDescription.length >= 5 ? 'text-green-600' : 'text-slate-500'
                  }`}>
                    {pauseDescription.length} characters
                  </p>
                  <p className="text-xs text-slate-500">
                    Minimum: 5 characters
                  </p>
                </div>
              </div>
            )}

            {isPaused && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">
                  ⏱️ Paused for: {pauseStartTime ? Math.floor((Date.now() - pauseStartTime) / 60000) : 0} minutes
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Maximum pause duration: 30 minutes. You'll be automatically resumed after 30 minutes.
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseDescription('');
                }}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition"
              >
                Cancel
              </button>
              {isPaused ? (
                <button
                  onClick={handleResumeTracking}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
                >
                  Resume Tracking
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSubmitPause}
                    disabled={pauseDescription.trim().length < 5}
                    className="flex-1 px-4 py-2.5 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Pause
                  </button>
                  <button
                    onClick={handleStopTracking}
                    disabled={pauseDescription.trim().length < 5}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Stop
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showPermissionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 relative z-[10000]">
            <div className="text-center mb-6">
              <MapPinIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Location Permission Required</h3>
              <p className="text-sm text-slate-600">
                This app needs access to your location for live tracking. Your location data is secure and used only for work purposes.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => handlePermissionResponse(false)}
                className="flex-1 px-4 py-3 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition flex items-center justify-center gap-2"
              >
                <XMarkIcon className="h-5 w-5" />
                Don't Allow
              </button>
              <button
                onClick={() => handlePermissionResponse(true)}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2"
              >
                <CheckCircleIcon className="h-5 w-5" />
                Allow Location
              </button>
            </div>
            
            <p className="text-xs text-slate-500 text-center mt-4">
              Your manager will be notified if location access is denied.
            </p>
          </div>
        </div>
      )}

      {/* Selfie Modal */}
      {showSelfieModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 relative z-[10000]">
            <canvas ref={canvasRef} className="hidden" />
            
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Attendance Selfie</h3>
              <button
                onClick={() => {
                  setShowSelfieModal(false);
                  setCapturedSelfie(null);
                  stopWebcam();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="relative aspect-[4/3] bg-gray-950 rounded-2xl overflow-hidden mb-5 border border-gray-100">
              {capturedSelfie ? (
                <img src={capturedSelfie} alt="Captured Selfie" className="w-full h-full object-cover" />
              ) : isWebcamActive ? (
                <>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-full object-cover scale-x-[-1]" 
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[180px] h-[240px] rounded-[50%] border-2 border-dashed border-white/50 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]" />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full space-y-3 px-6 text-center">
                  {selfieLoading ? (
                    <div className="animate-pulse flex space-x-2">
                      <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
                      <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
                      <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-900/50 rounded-full mb-2">
                      {hasCamera ? <CameraIcon className="h-8 w-8 text-gray-600" /> : <VideoCameraSlashIcon className="h-8 w-8 text-rose-400" />}
                    </div>
                  )}
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-widest leading-relaxed">
                    {selfieLoading ? 'Initializing Camera...' : 
                     !hasCamera ? 'Camera not available' : 
                     'Click to start camera for attendance verification'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => handleCheckInWithSelfie(null)}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition"
              >
                Skip Selfie
              </button>
              {capturedSelfie ? (
                <button
                  onClick={() => handleCheckInWithSelfie(capturedSelfie)}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
                >
                  Check In
                </button>
              ) : isWebcamActive ? (
                <button
                  onClick={captureSelfie}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
                >
                  Capture
                </button>
              ) : (
                <button
                  onClick={startWebcam}
                  disabled={!hasCamera || selfieLoading}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {selfieLoading ? 'Starting...' : 'Start Camera'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function LocationHistory() {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-500">Loading live tracking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 max-w-5xl mx-auto" style={{ fontFamily: "Poppins, sans-serif" }}>
      <EmployeeLiveTracking />
    </div>
  );
}
